import { currencyLocaleMap } from '../constants/currencyLocaleMap';

const zeroDecimalCurrencyList = [
  'MGA',
  'BIF',
  'CLP',
  'PYG',
  'DJF',
  'RWF',
  'GNF',
  'JPY',
  'VND',
  'VUV',
  'XAF',
  'KMF',
  'KRW',
  'XOF',
  'XPF'
];

interface FormatCurrencyOptions {
  decimalPlaces?: number;
  notation?: 'compact' | 'standard';
  alwaysShowDecimals?: boolean;
}

export default class CurrencyHelper {
  // Coerce nullish/empty codes to a safe default. JS callers can still pass
  // `null`/`''` (which bypass a default parameter), and Intl throws on those.
  // Normalising to uppercase keeps the locale/zero-decimal lookups working when
  // a caller supplies a lower-case code like "aud".
  private static normaliseCode = (code?: string | null): string =>
    (code || 'AUD').toUpperCase();

  static formatCurrency = (
    value: number,
    code?: string | null,
    options?: FormatCurrencyOptions
  ) => {
    const safeCode = CurrencyHelper.normaliseCode(code);
    const isZeroDecimal =
      zeroDecimalCurrencyList.includes(safeCode) || options?.decimalPlaces === 0;
    const hasDecimals = !isZeroDecimal && value % 1 !== 0;
    const forceDecimals = options?.alwaysShowDecimals && !isZeroDecimal;

    return new Intl.NumberFormat(currencyLocaleMap[safeCode], {
      style: 'currency',
      currency: safeCode,
      currencyDisplay: 'narrowSymbol',
      notation: options?.notation || 'standard',
      minimumFractionDigits: forceDecimals ? 2 : hasDecimals ? 2 : 0,
      maximumFractionDigits: isZeroDecimal ? 0 : 2
    }).format(value);
  };

  static getCurrencySymbol = (code?: string | null): string => {
    const safeCode = CurrencyHelper.normaliseCode(code);
    try {
      const parts = new Intl.NumberFormat(currencyLocaleMap[safeCode], {
        style: 'currency',
        currency: safeCode,
        currencyDisplay: 'narrowSymbol'
      }).formatToParts(0);

      return parts.find((p) => p.type === 'currency')?.value ?? safeCode;
    } catch {
      return safeCode;
    }
  };

  static roundToDecimal = (value: number, decimalPlaces: number) => {
    return (
      Math.round(value * Math.pow(10, decimalPlaces)) /
      Math.pow(10, decimalPlaces)
    );
  };
}
